import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { AddVoiceoverSceneTool } from './AddVoiceoverSceneTool';
import { DeleteSceneTool } from './DeleteSceneTool';
import { resolveOverlayWordRangeOperation, setSceneRangeOperation } from './overlay-placement';
import { resolveSceneHandle, sceneDisplayId } from './scene-addressing';
import { SetSceneDurationTool } from './SetSceneDurationTool';
import { partitionTranscriptByScene } from './transcript-partition';
import { VideoStateTool } from './VideoStateTool';

import { SCENE_GROUP_TYPES } from 'js/src/agent/video-state/constants';
import { getSceneGroupType } from 'js/src/agent/video-state/serialization/assetSerializer';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { isSceneAnOverlay } from 'js/src/luminary/luminary-selectors';

// Slack (ms) so a target that just touches the current coverage isn't rejected over snap jitter.
const ADJACENCY_EPSILON_MILLIS = 1;

// The scene group types set_scene_range applies to: every non-regular type, from the enum.
const VOICE_DRIVEN_SCENE_GROUP_TYPES = Object.values(SCENE_GROUP_TYPES).filter(
    (type) => type !== SCENE_GROUP_TYPES.REGULAR,
);

export const setSceneRangeSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    words: z
        .array(z.string())
        .describe(
            'Consecutive transcript tokens the scene should cover, spelled exactly as they appear (include any `<pause seconds="N"/>` between them).',
        ),
});
export type SetSceneRangeArgs = z.infer<typeof setSceneRangeSchema>;

export class SetSceneRangeTool extends VideoStateTool<typeof setSceneRangeSchema> {
    public static readonly toolName = 'set_scene_range';

    protected override readonly schema = setSceneRangeSchema;
    protected override readonly toolName = SetSceneRangeTool.toolName;
    protected override readonly toolDescription =
        'Re-time an existing voiceover scene so it covers exactly the given transcript words — use to grow, shrink, or shift a voiceover scene.';

    public override getToolDisplayName(): string {
        return 'Re-timing scene';
    }

    public get statusIcon(): string {
        return 'straighten';
    }

    public async execute(args: SetSceneRangeArgs, context: ToolExecutionContext) {
        const { words } = args;
        const { parentActionGroup } = context;

        try {
            const state = this.getState();
            const { scene, sceneShortId } = resolveSceneHandle(this.luminary, state, args);

            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup) {
                return `Error: No scene group found for scene ${sceneShortId}`;
            }
            if (getSceneGroupType(sceneGroup) === SCENE_GROUP_TYPES.REGULAR) {
                return `Error: ${SetSceneRangeTool.toolName} only re-times scenes on ${VOICE_DRIVEN_SCENE_GROUP_TYPES.join(', ')} scene groups. Scene ${sceneShortId} is on a ${SCENE_GROUP_TYPES.REGULAR} scene group — use ${SetSceneDurationTool.toolName} to change its length.`;
            }
            if (!isSceneAnOverlay(state, scene)) {
                return `Error: Scene ${sceneShortId} is the base/presenter scene of a voice-driven scene group, which carries the voice-over and controls the group's timing, so its range can't be set. Re-time a voiceover scene instead.`;
            }
            if (!words || words.length === 0) {
                return 'Error: words array cannot be empty. Provide the transcript words the scene should cover.';
            }

            const { resolveOverlayWordRange } = makeOperationsCallable(
                { resolveOverlayWordRangeOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            const range = resolveOverlayWordRange(sceneGroup, words);
            if ('error' in range) {
                return `Error: ${range.error}`;
            }
            const { startTimeMillis, endTimeMillis } = range;

            // Must overlap (or touch) the scene's current coverage — this resizes in place, it doesn't
            // teleport. A disjoint target is almost always a mistake; delete + re-add is the way to move.
            const partition = partitionTranscriptByScene(state, sceneGroup);
            const current = partition?.find((entry) => entry.scene.id === scene.id);
            if (current) {
                const disjoint =
                    endTimeMillis < current.startMillis - ADJACENCY_EPSILON_MILLIS ||
                    startTimeMillis > current.endMillis + ADJACENCY_EPSILON_MILLIS;
                if (disjoint) {
                    return `Error: The words "${words.join(' ')}" don't overlap scene ${sceneShortId}'s current coverage, so this would move the scene rather than resize it. To place a scene somewhere unrelated, ${DeleteSceneTool.toolName} and then ${AddVoiceoverSceneTool.toolName} there instead.`;
                }
            }

            // Refuse a range that fully engulfs another overlay: clearing the target region would
            // delete it outright (with its content). Resizing only pushes against the adjacent scene;
            // removing an overlay is delete_scene's job.
            const engulfed = (partition ?? []).filter(
                (entry) =>
                    entry.role === 'overlay' &&
                    entry.scene.id !== scene.id &&
                    startTimeMillis - ADJACENCY_EPSILON_MILLIS <= entry.startMillis &&
                    entry.endMillis <= endTimeMillis + ADJACENCY_EPSILON_MILLIS,
            );
            if (engulfed.length > 0) {
                const list = engulfed
                    .map(
                        (entry) =>
                            `${sceneDisplayId(this.luminary, entry.scene.id)} ("${entry.words.join(' ')}")`,
                    )
                    .join(', ');
                return `Error: The words "${words.join(' ')}" fully span scene ${list}, so re-timing ${sceneShortId} onto them would delete ${engulfed.length > 1 ? 'those scenes' : 'that scene'} and ${engulfed.length > 1 ? 'their' : 'its'} content. ${SetSceneRangeTool.toolName} only resizes against the neighbouring coverage — it won't swallow a whole scene. Extend ${sceneShortId} only as far as the adjacent scene, or ${DeleteSceneTool.toolName} the scene(s) you mean to replace first.`;
            }

            const { setSceneRange } = makeOperationsCallable(
                { setSceneRangeOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            setSceneRange(
                scene.id,
                sceneGroup.id,
                startTimeMillis,
                endTimeMillis,
                false, // systemTriggered
                parentActionGroup,
            );

            return `Success: Scene ${sceneShortId} now covers "${words.join(' ')}". Neighbouring coverage reflowed to match — check the diff for the resulting scene boundaries.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting scene range: ${errorMessage}`;
        }
    }
}
